// src/pages/DocumentationPage.tsx — Site officiel Vanessa : référence de l'API
import { Link } from 'react-router-dom';
import { Container, PageHeader, Section, CodeBlock, InlineCode } from '@/components/site/blocks';
import { VANESSA_API_URL } from '@/api/constants';

const ERRORS: [string, string][] = [
    ['400', 'Requête mal formée (voir le message d’erreur retourné).'],
    ['401', 'Clé API manquante ou invalide.'],
    ['403', 'Clé en attente de validation ou suspendue.'],
    ['429', 'Trop de requêtes (plus de 20 par minute) ou quota de tokens épuisé. Patientez ou rechargez depuis la console.'],
];

const H2 = ({ id, children }: { id: string; children: React.ReactNode }) => (
    <h2 id={id} className="mt-12 scroll-mt-24 font-display text-2xl font-bold text-ink first:mt-0">{children}</h2>
);
const P = ({ children }: { children: React.ReactNode }) => <p className="mt-3 text-lg leading-relaxed text-gray-700">{children}</p>;

const TOC = [
    ['auth', 'Authentification'], ['endpoint', 'Point d’accès'], ['requete', 'Requête'], ['context', 'Le champ context'],
    ['reponse', 'Réponse'], ['historique', 'Historique'], ['limites', 'Limites'], ['erreurs', 'Erreurs'], ['bonnes-pratiques', 'Bonnes pratiques'],
];

export default function DocumentationPage() {
    const apiUrl = VANESSA_API_URL || 'https://api.kinemaplus.com';

    return (
        <>
            <PageHeader eyebrow="Développeurs" title="Documentation de l’API" intro="Tout ce qu’il faut pour intégrer Vanessa dans votre projet." />
            <Section>
                <Container className="!px-0">
                    <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
                        <nav aria-label="Sur cette page" className="lg:sticky lg:top-24 lg:self-start">
                            <p className="mb-2 font-display font-semibold text-ink">Sur cette page</p>
                            <ul className="flex flex-wrap gap-2 lg:block lg:space-y-1">
                                {TOC.map(([id, label]) => (
                                    <li key={id}><a href={`#${id}`} className="inline-block rounded-full px-3 py-1 text-base text-gray-700 hover:bg-brand-tint lg:block lg:rounded-lg lg:px-2">{label}</a></li>
                                ))}
                            </ul>
                        </nav>

                        <div className="min-w-0 max-w-3xl">
                            <H2 id="auth">Authentification</H2>
                            <P>
                                Chaque requête doit inclure votre clé API dans l’en-tête <InlineCode>x-api-key</InlineCode>.
                                Récupérez-la depuis votre <Link to="/console" className="font-semibold text-ochre underline">console développeur</Link>.
                            </P>

                            <H2 id="endpoint">Point d’accès</H2>
                            <CodeBlock label="HTTP">{`POST ${apiUrl}/v1`}</CodeBlock>
                            <P>Utilisez toujours le chemin <InlineCode>/v1</InlineCode> : votre intégration continuera de fonctionner même si une version future de l’API change.</P>

                            <H2 id="requete">Requête</H2>
                            <P>Un tableau de messages, comme sur la plupart des API conversationnelles. Le premier message doit avoir le rôle <InlineCode>user</InlineCode>.</P>
                            <CodeBlock label="JSON">{`{
  "messages": [
    { "role": "user", "content": "Salut Vanessa, comment ça va ?" }
  ],
  "context": "...",     // optionnel, 2000 caractères maximum
  "max_tokens": 300     // optionnel, 300 par défaut, 1024 maximum
}`}</CodeBlock>

                            <H2 id="context">Le champ <InlineCode>context</InlineCode> : cadrer une conversation</H2>
                            <P>Un texte libre (2000 caractères maximum) qui s’ajoute aux instructions de Vanessa, pour cadrer une conversation autour d’un thème ou lui fournir un contenu qu’elle ne connaît pas (un article, un sujet précis).</P>
                            <P><strong className="text-ink">Ce n’est pas un champ « system » classique.</strong> Il ne peut jamais remplacer ni désactiver le ton, le style ou les garde-fous de Vanessa : il s’ajoute en complément. Vanessa reste elle-même, avec sa personnalité et ses limites de sécurité, quel que soit le site où elle est intégrée.</P>
                            <CodeBlock label="JSON">{`{
  "messages": [
    { "role": "user", "content": "Raconte-moi une histoire" }
  ],
  "context": "Cadre toutes tes réponses autour du thème jeunes et aventures."
}`}</CodeBlock>

                            <H2 id="reponse">Réponse</H2>
                            <CodeBlock label="JSON">{`{
  "reply": "Wèèh ça va, et toi ?",
  "usage": {
    "input_tokens": 24,
    "output_tokens": 18,
    "total_tokens": 42,
    "tokens_remaining": 999958
  }
}`}</CodeBlock>
                            <P>Le champ <InlineCode>tokens_remaining</InlineCode> indique le quota restant sur votre clé après cette requête.</P>

                            <H2 id="historique">Conserver l’historique</H2>
                            <P>L’API est sans état : renvoyez l’historique complet à chaque appel pour que Vanessa garde le fil, en alternant les rôles <InlineCode>user</InlineCode> et <InlineCode>assistant</InlineCode>.</P>
                            <CodeBlock label="JSON">{`{
  "messages": [
    { "role": "user", "content": "Salut Vanessa !" },
    { "role": "assistant", "content": "Wèèh ça va, et toi ?" },
    { "role": "user", "content": "Ça va, dis-moi un truc drôle" }
  ]
}`}</CodeBlock>

                            <H2 id="limites">Limites</H2>
                            <ul className="mt-3 list-disc space-y-1 pl-6 text-lg text-gray-700">
                                <li><strong className="text-ink">20 requêtes par minute</strong> et par clé.</li>
                                <li><InlineCode>max_tokens</InlineCode> : 300 par défaut, 1024 au maximum.</li>
                                <li><InlineCode>context</InlineCode> : 2000 caractères au maximum.</li>
                                <li>Quota de tokens propre à chaque clé, rechargeable à tout moment.</li>
                            </ul>

                            <H2 id="erreurs">Erreurs</H2>
                            <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
                                <table className="w-full border-collapse text-left text-base">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-sand text-sm text-gray-700">
                                            <th scope="col" className="px-4 py-3">Code</th>
                                            <th scope="col" className="px-4 py-3">Signification</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ERRORS.map(([code, text]) => (
                                            <tr key={code} className="border-b border-gray-100 last:border-0">
                                                <td className="px-4 py-3 font-mono font-semibold text-ink">{code}</td>
                                                <td className="px-4 py-3 text-gray-700">{text}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <H2 id="bonnes-pratiques">Bonnes pratiques</H2>
                            <ul className="mt-3 list-disc space-y-1 pl-6 text-lg text-gray-700">
                                <li>Appelez l’API depuis votre serveur : une clé placée dans du code public peut être volée.</li>
                                <li>Gérez le code 429 en attendant quelques secondes avant de réessayer.</li>
                                <li>Indiquez clairement à vos utilisateurs qu’ils parlent à une intelligence artificielle.</li>
                                <li>Limitez la taille de l’historique renvoyé pour économiser vos tokens.</li>
                            </ul>
                            <P>Un exemple complet en trois langages se trouve dans le <Link to="/developpeurs/demarrage" className="font-semibold text-ochre underline">démarrage rapide</Link>.</P>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
}
